import React from 'react';
import Marquee from "react-fast-marquee";
import { Box, Typography, Link, useTheme, Paper } from '@mui/material';
import { TickersForBanner, BannerTickerItem } from '../../types/investment'; // Assuming types are defined
import { formatPercent } from '../../utils/formatters'; // Assuming you have this formatter
import { alpha } from '@mui/material/styles';
import { getSignalColor } from '../../theme/themesV3';

interface TickerBannerProps {
  bannerData: TickersForBanner | null;
  onTickerSelect: (ticker: string) => void;
  isLoading?: boolean;
  error?: string | null;
}

const TickerItem: React.FC<{ item: BannerTickerItem; onClick: () => void }> = ({ item, onClick }) => {
  const theme = useTheme();
  const isPositive = item.change >= 0;
  const changeColor = isPositive ? getSignalColor('Bullish') : getSignalColor('Bearish');

  return (
    <Link
      component="button" // Make it behave like a button for accessibility/semantics
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'baseline', // Align text nicely
        mx: 2, // Horizontal margin between items
        textDecoration: 'none',
        //color: 'inherit',
        color: theme.palette.custom.tickerBannerText,
        cursor: 'pointer',
        border: 'none', // Remove button border
        background: 'none', // Remove button background
        p: 0, // Remove button padding
        font: 'inherit', // Inherit font styles
        '&:hover': {
          opacity: 0.8,
          color: theme.palette.custom.tickerBannerHoverText,
        },
        transition: 'color 0.2s ease-in-out',
      }}
    >
      <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', mr: 0.5 }}>
        {item.ticker}
      </Typography>
      <Typography variant="body2" component="span" sx={{ mr: 0.5 }}>
        ${item.close.toFixed(2)}
      </Typography>
      <Typography variant="body2" component="span" sx={{ color: changeColor, fontWeight: 'medium' }}>
        {isPositive ? '+' : ''}{formatPercent(item.change / 100)}
      </Typography>
    </Link>
  );
};

const TickerBanner: React.FC<TickerBannerProps> = ({ bannerData, onTickerSelect, isLoading, error }) => {
  const theme = useTheme();

  const bannerBgColor = theme.palette.mode === 'dark' 
    ? theme.palette.custom.tickerBannerBg  // Dark mode: use darker background paper
    : theme.palette.custom.tickerBannerBg;     // Light mode: use primary color (dark navy)

  const textColor = theme.palette.mode === 'dark'
    ? theme.palette.custom.tickerBannerText      // Dark mode: use default text
    : theme.palette.custom.tickerBannerText; // Light mode: use theme contrast text instead of hardcoded white

  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          py: 0.5, // Reduced padding
          px: 1,
          overflow: 'hidden',
          backgroundColor: bannerBgColor,
          color: textColor,
          borderBottom: 'none',
          height: '36px', // Fixed height matching the banner
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            fontStyle: 'italic', 
            color: theme.palette.mode === 'dark' 
              ? theme.palette.text.secondary 
              : alpha(theme.palette.primary.contrastText, 0.7), 
            width: '100%', 
            textAlign: 'center' 
          }}
        >
          Loading market banner...
        </Typography>
      </Paper>
    );
  }

  if (error || !bannerData || (!bannerData.MarketETFs?.length && !bannerData.SectorETFs?.length && !bannerData.SelectedTickers?.length)) {
    // Don't render anything or show a subtle error/empty state if needed
    return null; // Or a minimal placeholder
  }

  const allTickers = [
    ...(bannerData.MarketETFs || []),
    ...(bannerData.SectorETFs || []),
    ...(bannerData.SelectedTickers || []),
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        py: 0.5, // Reduced padding
        overflow: 'hidden',
        backgroundColor: bannerBgColor,
        color: textColor,
        borderBottom: 'none',
        height: '36px', // Fixed height
      }}
    >
      <Marquee
        gradient={false} // Optional: disable gradient overlay
        speed={40} // Adjust speed as needed
        pauseOnHover={true}
      >
        {allTickers.map((item, index) => (
          <TickerItem key={`${item.ticker}-${index}`} item={item} onClick={() => onTickerSelect(item.ticker)} />
        ))}
        {/* Add a separator visually if needed, Marquee handles spacing */}
        <Box sx={{ width: '50px' }} /> {/* Add extra space at the end before loop */}
      </Marquee>
    </Paper>
  );
};

export default TickerBanner;
